import { useEffect, useState } from "react";

import { type WordCallTracker, wordCallTracker } from "../utils/word-responsiveness";

export const WORD_RESPONSIVENESS_POLL_INTERVAL_MS = 1_000;

export interface WordResponsiveness {
    isSlow: boolean;
    oldestCallAgeMsWhenSlow: number | null;
}

const RESPONDING_NORMALLY: WordResponsiveness = { isSlow: false, oldestCallAgeMsWhenSlow: null };

const responsivenessOf = (tracker: WordCallTracker): WordResponsiveness => {
    if (!tracker.isWordRespondingSlowly()) return RESPONDING_NORMALLY;
    return { isSlow: true, oldestCallAgeMsWhenSlow: tracker.oldestInFlightCallAgeMs() };
};

export const useWordResponsiveness = (tracker: WordCallTracker = wordCallTracker): WordResponsiveness => {
    const [responsiveness, setResponsiveness] = useState<WordResponsiveness>(() => responsivenessOf(tracker));

    useEffect(() => {
        let pollTimer: ReturnType<typeof setInterval> | null = null;

        const refreshResponsiveness = () => {
            const latest = responsivenessOf(tracker);
            setResponsiveness((previous) => (previous.isSlow === latest.isSlow ? previous : latest));
        };

        const pollOnlyWhileACallIsInFlight = () => {
            const aCallIsInFlight = tracker.inFlightCallCount() > 0;
            if (aCallIsInFlight && pollTimer === null) {
                pollTimer = setInterval(refreshResponsiveness, WORD_RESPONSIVENESS_POLL_INTERVAL_MS);
            }
            if (!aCallIsInFlight && pollTimer !== null) {
                clearInterval(pollTimer);
                pollTimer = null;
            }
        };

        const unsubscribe = tracker.subscribe(() => {
            refreshResponsiveness();
            pollOnlyWhileACallIsInFlight();
        });
        refreshResponsiveness();
        pollOnlyWhileACallIsInFlight();

        return () => {
            unsubscribe();
            if (pollTimer !== null) clearInterval(pollTimer);
        };
    }, [tracker]);

    return responsiveness;
};
